/**
 * ContratsEvolution — Timeline créations vs résiliations + MRR net.
 */
import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { TrendingUp } from "lucide-react";
import {
  LineChart, Line, ComposedChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Legend,
} from "recharts";
import { toast } from "sonner";

const fmtEUR = (v) => new Intl.NumberFormat("fr-FR",
  { style: "currency", currency: "EUR", minimumFractionDigits: 0 }).format(v || 0);

export default function ContratsEvolution() {
  const [timeline, setTimeline] = useState([]);
  const [evolution, setEvolution] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [tl, ev] = await Promise.all([
          api.get("/contracts/stats/mrr-timeline?months=12"),
          api.get("/contracts/stats/evolution?months=12"),
        ]);
        setTimeline(tl.data.timeline || []);
        setEvolution(ev.data.timeline || []);
      } catch (e) { toast.error(apiError(e)); }
    })();
  }, []);

  return (
    <div className="space-y-6" data-testid="contrats-evolution-page">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight text-slate-900 flex items-center gap-2"
            style={{ fontFamily: "Manrope, sans-serif" }}>
          <TrendingUp className="h-7 w-7 text-blue-600" /> Évolution
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Suivi 12 mois : évolution du MRR net et des mouvements du portefeuille.
        </p>
      </div>

      <Card>
        <CardContent className="p-5">
          <h2 className="text-lg font-semibold mb-4">MRR net (12 mois)</h2>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={timeline}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="month" fontSize={11} />
              <YAxis fontSize={11} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => fmtEUR(v)} />
              <Legend />
              <Line type="monotone" dataKey="mrr_client" stroke="#10b981" strokeWidth={2} name="MRR clients" />
              <Line type="monotone" dataKey="mrr_supplier" stroke="#f59e0b" strokeWidth={2} name="MRR fournisseurs" />
              <Line type="monotone" dataKey="mrr_net" stroke="#3b82f6" strokeWidth={2.5} name="MRR net" />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-5">
          <h2 className="text-lg font-semibold mb-4">Mouvements du portefeuille</h2>
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={evolution}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="month" fontSize={11} />
              <YAxis fontSize={11} allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Bar dataKey="created" fill="#10b981" name="Créations" />
              <Bar dataKey="cancelled" fill="#ef4444" name="Résiliations" />
            </ComposedChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
}
